/**
 * TypeScript types for the items system.
 */

import { StatKey, SkillKey } from './characters';

/**
 * Bonus key type (stat or skill).
 */
export type BonusKey = StatKey | SkillKey;

/**
 * Grouped bonus labels for item configuration.
 */
export interface GroupedBonusLabels {
    stats: Record<string, string>;
    skills: Record<string, string>;
}

/**
 * Item rarity values.
 */
export type ItemRarityValue = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'artifact' | 'unique';

/**
 * Item rarity information.
 */
export interface ItemRarity {
    value: ItemRarityValue;
    label: string;
    color: string;
    tailwindClass: string;
    statCount: number | null;
    baseStatRange: [number, number] | null;
}

/**
 * Item stat with its value and label.
 */
export interface ItemStat {
    value: number;
    label: string;
}

/**
 * Image URLs for different variants.
 */
export interface ItemImage {
    small: string | null;
    medium: string | null;
    large: string | null;
}

/**
 * Tag for categorizing items.
 */
export interface Tag {
    id: number;
    name: string;
}

/**
 * Crafting material requirement.
 */
export interface CraftingMaterial {
    type: 'tea' | 'wood' | 'mystical' | 'medicinal' | 'edible';
    quantity: number;
}

/**
 * Building type for crafting requirements.
 */
export interface BuildingTypeOption {
    id: number;
    name: string;
    rank: number;
}

/**
 * How a recipe can be learned by players.
 * - 'no': Cannot be learned (GM-awarded only)
 * - 'blueprint': Requires finding and using a blueprint item
 * - 'building': Learned by constructing the required building
 */
export type LearnableBy = 'no' | 'blueprint' | 'building';

/**
 * Item recipe (template/blueprint for items).
 */
export interface ItemRecipe {
    id: number;
    name: string;
    slug: string;
    description: string | null;
    image: ItemImage;
    rarity: {
        value: ItemRarityValue;
        label: string;
        color: string;
        tailwindClass: string;
    };
    stat_count: number;
    base_stat_range: [number, number];
    fixed_stats: Record<BonusKey, number> | null;
    allowed_stats: BonusKey[] | null;
    required_building_type_id: number | null;
    required_building_level: number;
    required_building_stars: number;
    required_building_type: BuildingTypeOption | null;
    learnable_by: LearnableBy;
    crafting_materials: CraftingMaterial[] | null;
    items_count: number;
    created_by: string | null;
    created_at: string | null;
    tags: Tag[];
}

/**
 * Item instance (owned by a user).
 */
export interface Item {
    id: number;
    name: string;
    slug: string;
    description: string | null;
    image: ItemImage;
    rarity: {
        value: ItemRarityValue;
        label: string;
        color: string;
        tailwindClass: string;
    };
    quality: number;
    quality_stars: string;
    /** Whether this is a blueprint (learning material) vs a crafted item */
    is_blueprint: boolean;
    stats: Record<string, ItemStat>;
    total_stat_bonus: number;
    recipe_fixed_stats: Record<string, ItemStat> | null;
    recipe_allowed_stats: Record<string, string> | null;
    is_equipped: boolean;
    character_id: number | null;
    character_name: string | null;
    awarded_by: string | null;
    awarded_at: string | null;
    tags: Tag[];
}

/**
 * Item preview (for GM to see potential outcomes before awarding).
 */
export interface ItemPreview {
    quality: number;
    quality_stars: string;
    stat_count: number;
    stat_range: [number, number];
    possible_stats: string[];
    fixed_stats: Record<string, number> | null;
}

/**
 * User for item awarding.
 */
export interface ItemAwardUser {
    id: number;
    username: string;
    name: string;
}

/**
 * Pagination link from Laravel.
 */
export interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

/**
 * Paginated response from Laravel.
 * Laravel's paginate() returns a flat structure with pagination info directly on the object.
 */
export interface PaginatedResponse<T> {
    data: T[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    links: PaginationLink[];
}

/**
 * Props for the GM Items Index page.
 */
export interface GmItemsIndexProps {
    recipes: PaginatedResponse<ItemRecipe>;
    rarities: ItemRarity[];
    filters: {
        search: string | null;
        rarity: string | null;
    };
}

/**
 * Props for the GM Items Create/Edit page.
 */
export interface GmItemsFormProps {
    recipe?: ItemRecipe;
    sourceRecipe?: ItemRecipe;
    rarities: ItemRarity[];
    stats: GroupedBonusLabels;
    allTags: Tag[];
    buildingTypes: BuildingTypeOption[];
}

/**
 * Props for the GM Items Award page.
 */
export interface GmItemsAwardProps {
    recipes: ItemRecipe[];
    users: ItemAwardUser[];
    selectedRecipe: ItemRecipe | null;
    stats: Record<string, string>;
}

/**
 * Props for the GM Awarded Items page.
 */
export interface GmItemsAwardedProps {
    items: PaginatedResponse<Item>;
    filters: {
        search: string | null;
        quality: string | null;
    };
}

/**
 * Rarity color classes for Tailwind.
 */
export const RARITY_COLORS: Record<ItemRarityValue, string> = {
    common: 'text-gray-100',
    uncommon: 'text-green-400',
    rare: 'text-blue-400',
    epic: 'text-purple-400',
    legendary: 'text-orange-400',
    artifact: 'text-yellow-300',
    unique: 'text-cyan-400',
};

/**
 * Get the Tailwind color class for a rarity.
 */
export function getRarityColorClass(rarity: ItemRarityValue): string {
    return RARITY_COLORS[rarity] || 'text-gray-100';
}

/**
 * Get quality stars as a string.
 */
export function getQualityStars(quality: number): string {
    return '★'.repeat(quality);
}

